import { useCallback, useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import moment from "moment"
import { debounce } from "lodash"

import Autocomplete from "../Autocomplete"

import * as customerApis from "../../utilities/apis/customer"
import * as functions from "../../utilities/enumerations/functions"

export default function CustomerCard({ customer, setCustomer }) {
  const navigate = useNavigate()
  const { token, permissions } = useSelector(state => state.user)
  const mobileRef = useRef(null)
  const addressRef = useRef(null)
  const zoneRef = useRef(null)
  const [customers, setCustomers] = useState([])
  const [active, setActive] = useState(-1)

  const resetCustomer = () => {
    setCustomer(null)
    setCustomers([])
    addressRef.current.value = ""
  }

  const goDown = () => {
    setActive((previous) => {
      if (previous >= customers.length)
        previous = 0
      else
        previous += 1

      mobileRef.current.value = customers[previous]?.mobile || ""
      return previous
    })
  }

  const goUp = () => {
    setActive((previous) => {
      if (previous <= 0)
        previous = customers.length
      else
        previous -= 1

      mobileRef.current.value = customers[previous]?.mobile || ""
      return previous
    })
  }

  const handleChange = useCallback(debounce(async (e) => {
    try {
      const mobile = e.target.value
      if (mobile.length < 1) throw new Error("Type atleast 2 digits")

      const response = await customerApis.getCustomers(token, functions.paramsToQuery({ mobile }))
      if (!response.status) throw new Error(response.message)
      if (response.customers && response.customers.length === 0) throw new Error(response.message)

      setCustomers(response.customers)
    } catch (error) {
      resetCustomer()
    }
  }, 800), [])

  const handleSelect = (value) => {
    const customerResponse = customers.find((customer) => customer.mobile === value)
    if (!customerResponse) return

    setCustomer(customerResponse)

    mobileRef.current.value = customerResponse.mobile
    addressRef.current.value = customerResponse.address
    zoneRef.current.value = customerResponse.zone.name
    setCustomers([])
  }

  const handleInputKeyup = (e) => {
    if (e.which === 40) goDown()
    if (e.which === 38) goUp()
    if (e.which === 13) handleSelect(e.target.value)
  }

  const handleSubmit = () => {
    const mobile = mobileRef.current.value
    const address = addressRef.current.value
    const zone = zoneRef.current.value

    let isValid = true

    if (!mobile) {
      mobileRef.current.classList.add("border-danger")
      isValid = false
    }
    if (!address) {
      addressRef.current.classList.add("border-danger")
      isValid = false
    }
    if (!isValid) return

    sessionStorage.setItem("customer", JSON.stringify({ mobile, address, zone }))
    navigate("/dashboard/cart")
  }

  return (
    <div className="card rounded-4 shadow-none mb-3">
      <div className="card-body p-3">
        <p className="text-body-emphasis fw-bold">Find or Create Customer</p>
        <input ref={zoneRef} type="hidden" id="zone" />

        <div className={`form-autocomplete mb-3 ${customers.length ? "show" : "hide"}`}>
          <label htmlFor="mobile" className="form-label">Mobile #</label>
          <input ref={mobileRef} type="text" className="form-control rounded-3" id="mobile" placeholder="Type to search customer" onChange={handleChange} onKeyUp={handleInputKeyup} required autoFocus />

          <Autocomplete items={customers} itemKey="mobile" active={active} setActive={setActive} onClick={handleSelect} />
        </div>
        <div className="mb-3">
          <label htmlFor="address" className="form-label">Address</label>
          <input ref={addressRef} type="text" className="form-control rounded-3" id="address" required />
        </div>
        <button type="button" className="btn btn-theme w-100 rounded-3" onClick={handleSubmit}>Continue</button>
      </div>
    </div>
  )
}
